import Immutable from 'immutable';
import {
  CreateMappedSheetVersionInput,
  MappedSheetVersion,
  MappedSheetVersionList,
} from 'records/MappedSheetVersion';
import GQLRequest from 'utils/GQLRequest';

export type Measurement<TUnit> = {
  unit: TUnit;
  value: number;
};

export const createMappedSheetVersion = async (
  projectid: string,
  pageid: string,
  imagecalibrationconfig: CreateMappedSheetVersionInput
): Promise<MappedSheetVersion> => {
  const calibrationconfig = {
    ...imagecalibrationconfig,
    scale: {
      from: {
        unit: imagecalibrationconfig.scale.from.unit.toUpperCase(),
        value: imagecalibrationconfig.scale.from.value,
      },
      to: {
        unit: imagecalibrationconfig.scale.to.unit.toUpperCase(),
        value: imagecalibrationconfig.scale.to.value,
      },
    },
  };

  const { createMappedSheetVersion: response } = await new GQLRequest()
    .query(
      `
      mutation CreateMappedSheetVersion($projectid:String!, $pageid:String!, $calibrationconfig:CalibrationConfigInput!) {
        createMappedSheetVersion(projectid: $projectid,pageid: $pageid, calibrationconfig: $calibrationconfig) {
          mappedsheetversion {
            sheetversionid
            sheetid
            projectid
            pageid
            imagesource {
              url
              calibrationconfig {
                bearing
                corners {
                  topleft {
                    latitude
                    longitude
                  }
                  topright {
                    latitude
                    longitude
                  }
                  bottomright {
                    latitude
                    longitude
                  }
                  bottomleft {
                    latitude
                    longitude
                  }
                }
                centerpoint {
                  latitude
                  longitude
                }
                scale {
                  from {
                    value
                    unit
                  }
                  to {
                    value
                    unit
                  }
                }
                croppath {
                  x
                  y
                }
                creationtimestamp
              }
            }
          }
        }
      }
      `
    )
    .variables({ projectid, pageid, calibrationconfig })
    .json<{
      createMappedSheetVersion: { mappedsheetversion: MappedSheetVersion };
    }>();

  const { mappedsheetversion: mappedSheet } = response;

  const imageSource = mappedSheet.imagesource!;

  return MappedSheetVersion.deserialize({
    projectid: mappedSheet.projectid,
    sheetid: mappedSheet.sheetid,
    pageid: mappedSheet.pageid,
    imagesource: {
      type: 'image',
      url: imageSource.url,
      id: `${mappedSheet.pageid}-image-source`,
      calibrationconfig: imageSource.calibrationconfig,
    },
    rastersource: null,
  });
};

export const removeMappedSheetVersion = async (
  projectid: string,
  pageid: string
): Promise<string> => {
  const { removeMappedSheetVersion: response } = await new GQLRequest()
    .query(
      `
      mutation RemoveMappedSheetVersion($projectid:String!, $pageid:String!) {
        removeMappedSheetVersion(projectid: $projectid,pageid: $pageid)
      }
      `
    )
    .variables({ projectid, pageid })
    .json<{
      removeMappedSheetVersion: string;
    }>();

  return response;
};

export const getMappedSheetVersions = async (
  projectid: string
): Promise<MappedSheetVersionList> => {
  const { allMappedSheetVersions } = await new GQLRequest()
    .query(
      `
      query AllMappedSheetVersions($projectid: String!) {
        allMappedSheetVersions(projectid: $projectid) {
          mappedsheets {
            sheetversionid
            projectid
            sheetid
            pageid
            imagesource {
              url
              calibrationconfig {
                bearing
                corners {
                  topleft {
                    latitude
                    longitude
                  }
                  topright {
                    latitude
                    longitude
                  }
                  bottomright {
                    latitude
                    longitude
                  }
                  bottomleft {
                    latitude
                    longitude
                  }
                }
                centerpoint {
                  latitude
                  longitude
                }
                scale {
                  from {
                    value
                    unit
                  }
                  to {
                    value
                    unit
                  }
                }
                croppath {
                  x
                  y
                }
                creationtimestamp
              }
            }
          }
        }
      }
      `
    )
    .variables({ projectid })
    .json<{
      allMappedSheetVersions: { mappedsheets: MappedSheetVersion[] };
    }>();

  const { mappedsheets } = allMappedSheetVersions;

  return Immutable.List(
    mappedsheets.map((mappedSheet) => {
      const imageSource = mappedSheet.imagesource!;

      return MappedSheetVersion.deserialize({
        projectid: mappedSheet.projectid,
        sheetid: mappedSheet.sheetid,
        pageid: mappedSheet.pageid,
        imagesource: {
          type: 'image',
          url: imageSource.url,
          id: `${mappedSheet.pageid}-image-source`,
          calibrationconfig: imageSource.calibrationconfig,
        },
        rastersource: null,
      });
    })
  );
};

export default {
  createMappedSheetVersion,
  getMappedSheetVersions,
  removeMappedSheetVersion,
};
